import * as Yup from 'yup';
import { closeSnackbar } from 'notistack';
import React, { useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller, FormProvider } from 'react-hook-form';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  Box,
  Grid,
  Stack,
  Paper,
  Button,
  Select,
  Divider,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  CircularProgress,
} from '@mui/material';

import { paths } from 'src/routes/routes/paths';
import { useParams, useRouter } from 'src/routes/hooks';

import { updateAutomation, getAutomationDetails } from 'src/server/store/automation';

import { useSnackbar } from 'src/components/snackbar';
import LoadingScreen from 'src/components/loading-screen/loading-screen';

import AutomationRuleCreate from './automationRuleCreate';
import CampaignSelectionCard from './CampaignSelectionCard'; // Import this at the top

// --- Validation Schema ---
const schema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  description: Yup.string(),
  campaigns: Yup.array().when('campaignOptions', {
    is: (val) => val === 'ALL',
    then: (s) => s,
    otherwise: (s) => s.min(1, 'Select at least one campaign'),
  }),
  campaignOptions: Yup.string(),
  schedule: Yup.object().shape({
    startDate: Yup.string(),
    endDate: Yup.string(),
    frequency: Yup.string().required('Frequency is required'),
    dayOfWeek: Yup.number(),
    dayOfMonth: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .nullable(),
    weekOfMonth: Yup.number()
      .transform((value, originalValue) => (originalValue === '' ? undefined : value))
      .nullable(),
    monthlyMode: Yup.string(),
    time: Yup.string().required('Time is required'),
    timezone: Yup.string().required('Timezone is required'),
  }),
  executionType: Yup.string().required('Execution type is required'),
});

const defaultValues = {
  title: '',
  description: '',
  campaigns: [],
  schedule: {
    startDate: '',
    endDate: '',
    frequency: 'DAILY',
    dayOfWeek: 1,
    dayOfMonth: '',
    time: '',
    timezone: 'Asia/Kolkata',
    monthlyMode: 'BY_WEEK',
    weekOfMonth: '',
  },
  executionType: 'MANUAL',
  campaignOptions: 'CUSTOM',
};

export default function AutomationUpdate() {
  const dispatch = useDispatch();
  const router = useRouter();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const { automationDetails, loading } = useSelector((state) => ({
    automationDetails: state.automation.automationDetails || {},
    loading: state.automation.loading,
  }));

  // Fetch automation details on component mount
  useEffect(() => {
    if (id) {
      dispatch(getAutomationDetails({ credentials: { id, dispatch } }));
    }
  }, [id, dispatch]);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = methods;

  console.log('errors', errors);
  const frequency = watch('schedule.frequency');
  const [monthlyMode, setMonthlyMode] = useState('BY_WEEK');
  const [finalRuleTemplateId, setFinalRuleTemplateId] = useState(null);
  const [autoSaving, setAutoSaving] = useState({
    isSavingInStarted: false,
    isSavingInCompleted: false,
    isSavingInFailed: false,
    message: '',
  });

  // Populate form when automation details are loaded
  useEffect(() => {
    if (automationDetails && Object.keys(automationDetails).length > 0) {
      const automation = automationDetails;

      // Map API response to form values
      const formValues = {
        title: automation.title || '',
        description: automation.description || '',
        campaigns: automation.campaigns || [],
        campaignOptions: automation.campaignOptions || 'CUSTOM',
        schedule: {
          startDate: automation.schedule?.startDate || '',
          endDate: automation.schedule?.endDate || '',
          frequency: automation.schedule?.frequency || 'DAILY',
          dayOfWeek: automation.schedule?.dayOfWeek || 1,
          dayOfMonth: automation.schedule?.dayOfMonth || '',
          time: automation.schedule?.time || '',
          timezone: automation.schedule?.timezone || 'Asia/Kolkata',
          monthlyMode: automation.schedule?.monthlyMode || 'BY_WEEK',
          weekOfMonth: automation.schedule?.weekOfMonth || '',
        },
        executionType: automation.executionType || 'MANUAL',
      };

      // Set monthly mode for UI
      setMonthlyMode(automation.schedule?.monthlyMode || 'BY_WEEK');

      // Set rule template ID if exists
      if (automation.automationRuleTemplate) {
        setFinalRuleTemplateId(automation.automationRuleTemplate);
      }

      // Reset form with populated values
      reset(formValues);
    }
  }, [automationDetails, reset, dispatch, id]);

  // Effect to close snackbar after auto-saving completes
  React.useEffect(() => {
    if (autoSaving.isSavingInCompleted) {
      const timer = setTimeout(() => {
        closeSnackbar();
      }, 1000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoSaving.isSavingInCompleted]);

  const onSubmit = async (data) => {
    const schedule = { ...data.schedule };
    Object.keys(schedule).forEach((key) => {
      if (schedule[key] === '') {
        schedule[key] = null;
      }
    });
    switch (schedule?.frequency) {
      case 'DAILY':
        delete schedule.dayOfWeek;
        delete schedule.dayOfMonth;
        delete schedule.monthlyMode;
        break;

      case 'WEEKLY':
        delete schedule.dayOfMonth;
        delete schedule.monthlyMode;
        break;

      default:
        break;
    }
    const body = {
      ...data,
      schedule,
      campaignOptions: data.campaigns && data.campaigns.length > 0 ? 'CUSTOM' : 'ALL',
      campaigns: data.campaigns,
      automationRuleTemplate: finalRuleTemplateId,
    };

    if (finalRuleTemplateId) {
      try {
        await dispatch(updateAutomation({ credentials: { id, body, dispatch } })).unwrap();
        enqueueSnackbar('Automation updated successfully!', { variant: 'success' });
        // Redirect back to automation list
        router.push(`${paths.dashboard.automation}?sort=AUTOMATION`);
      } catch (error) {
        enqueueSnackbar(error || 'Failed to update automation', { variant: 'error' });
      }
    } else {
      setAutoSaving({
        ...autoSaving,
        isSavingInStarted: true,
        message: 'Saving process started...',
      });

      enqueueSnackbar(
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 250 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="overline" sx={{ mb: 1 }}>
              Saving rule template
            </Typography>
            <br />
            <Typography variant="caption" sx={{ mb: 1, color: 'text.secondary' }}>
              {autoSaving.message}
            </Typography>
            <LoadingScreen sx={{ px: 0, py: 0, minHeight: 0 }} />
          </Box>
        </Box>,
        {
          variant: 'info',
          persist: true,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          // Remove the action prop here!
        }
      );
    }
  };

  // Show loading while fetching automation details
  if (loading && Object.keys(automationDetails).length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container xs={12} spacing={2} mb={3} sx={{ position: 'relative' }}>
          <Grid item xs={6} spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Update Automation
              </Typography>
            </Grid>
            <Grid item xs={12} mb={2}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Title"
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    size="small"
                    required
                    InputLabelProps={{ required: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Description"
                    fullWidth
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    size="small"
                    multiline
                    rows={4}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid item xs={6}>
            <Box
              sx={{
                position: 'fixed',
                top: 100,
                right: 40,
                zIndex: 10,
                background: 'transparent',
                py: 2,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                type="submit"
                color="success"
                variant="contained"
                size="medium"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Automation'}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 0 }}>
              Campaigns <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Typography variant="caption" sx={{ mb: 1 }}>
              Select the campaigns you want to automate.
            </Typography>
            <Controller
              name="campaigns"
              control={control}
              render={({ field }) => (
                <CampaignSelectionCard
                  value={field.value}
                  onChange={field.onChange}
                  setValue={setValue}
                  watch={watch}
                />
              )}
            />
            {errors.campaigns && (
              <Typography color="error" variant="caption">
                {errors.campaigns.message}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} mt={2}>
            <AutomationRuleCreate
              autoSaving={autoSaving}
              type="minimal"
              setFinalRuleTemplateId={setFinalRuleTemplateId}
              setAutoSaving={setAutoSaving}
              ruleTemplateId={automationDetails?.automationRuleTemplate}
            />
          </Grid>

          {/* Schedule (Advanced Settings) */}
          <Grid item xs={12} mt={2}>
            <Stack mb={1}>
              <Typography variant="subtitle2" sx={{ mb: 0 }}>
                Schedule <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography variant="caption" sx={{ mb: 1 }}>
                advanced settings, set the frequency and timezone for the automation.
              </Typography>
            </Stack>

            <Paper elevation={0} sx={{ p: 3, border: '1px solid #eee', borderRadius: 2 }}>
              {/* Frequency */}
              <Stack mb={1}>
                <Typography variant="body2" sx={{ mb: 0 }} fontWeight={600}>
                  Frequency
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Set how often the rule should check the criteria
                </Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Controller
                  name="schedule.frequency"
                  control={control}
                  render={({ field }) => (
                    <FormControl size="small" sx={{ minWidth: 180 }} required>
                      <Select
                        className="my-custom-selector"
                        size="small"
                        {...field}
                        displayEmpty
                        inputProps={{ required: true }}
                      >
                        <MenuItem value="DAILY">Daily</MenuItem>
                        <MenuItem value="EVERY_2_DAYS">Every 2 days</MenuItem>
                        <MenuItem value="EVERY_3_DAYS">Every 3 days</MenuItem>
                        <MenuItem value="EVERY_4_DAYS">Every 4 days</MenuItem>
                        <MenuItem value="EVERY_5_DAYS">Every 5 days</MenuItem>
                        <MenuItem value="EVERY_6_DAYS">Every 6 days</MenuItem>
                        <MenuItem value="WEEKLY">Weekly</MenuItem>
                        <MenuItem value="BI_WEEKLY">Bi Weekly</MenuItem>
                        <MenuItem value="MONTHLY">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
                {frequency && (
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 0 }}>
                    {/* Show Every & Unit only for WEEKLY and MONTHLY */}
                    {frequency === 'MONTHLY' && (
                      <>
                        {frequency === 'MONTHLY' && (
                          <Controller
                            name="schedule.monthlyMode"
                            control={control}
                            defaultValue="BY_WEEK"
                            render={({ field }) => (
                              <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
                                <Select
                                  value={monthlyMode}
                                  onChange={(e) => setMonthlyMode(e.target.value)}
                                  size="small"
                                  className="my-custom-selector"
                                >
                                  <MenuItem value="BY_WEEK">By week</MenuItem>
                                  <MenuItem value="BY_DATE">By date</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                          />
                        )}
                        {/* By week logic */}
                        {frequency === 'MONTHLY' && monthlyMode === 'BY_WEEK' && (
                          <>
                            <Typography variant="body2">Every</Typography>
                            <Controller
                              // todo : selected weekOfMonth is not sohiwng in dropdown
                              name="schedule.weekOfMonth"
                              control={control}
                              defaultValue={1}
                              render={({ field }) => (
                                <FormControl size="small" sx={{ minWidth: 80, ml: 1 }}>
                                  <Select
                                    value={field.value || 1}
                                    onChange={(e) => {
                                      const val = Number(e.target.value);
                                      field.onChange(val);
                                    }}
                                    size="small"
                                    className="my-custom-selector"
                                  >
                                    <MenuItem value={1}>1st</MenuItem>
                                    <MenuItem value={2}>2nd</MenuItem>
                                    <MenuItem value={3}>3rd</MenuItem>
                                    <MenuItem value={4}>4th</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            />

                            <Typography variant="body2">Week</Typography>
                          </>
                        )}
                        {/* By date logic */}
                        {frequency === 'MONTHLY' && monthlyMode === 'BY_DATE' && (
                          <>
                            <Typography variant="body2">Every</Typography>
                            <Controller
                              name="schedule.dayOfMonth"
                              control={control}
                              defaultValue={1}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  type="number"
                                  size="small"
                                  sx={{ width: 80 }}
                                  inputProps={{ min: 1, max: 31 }}
                                  value={field.value ?? 1}
                                  onChange={(e) => {
                                    let val = e.target.value === '' ? '' : Number(e.target.value);
                                    if (val !== '') {
                                      val = Math.max(1, Math.min(31, val));
                                    }
                                    // Always pass a number or empty string (for controlled input)
                                    field.onChange(val === '' ? '' : Number(val));
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <Typography
                                        variant="body2"
                                        sx={{ color: 'text.secondary', ml: 0.5 }}
                                      >
                                        th
                                      </Typography>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </>
                        )}
                      </>
                    )}
                    {/* Show Day of Week only for WEEKLY */}
                    {(frequency === 'WEEKLY' ||
                      frequency === 'BI_WEEKLY' ||
                      (frequency === 'MONTHLY' && monthlyMode === 'BY_WEEK')) && (
                      <>
                        <Typography variant="body2">on</Typography>
                        <Controller
                          name="schedule.dayOfWeek"
                          control={control}
                          defaultValue={1}
                          render={({ field }) => (
                            <FormControl size="small" sx={{ minWidth: 180, p: 0 }}>
                              <Select
                                className="my-custom-selector"
                                {...field}
                                displayEmpty
                                onChange={(e) => field.onChange(Number(e.target.value))}
                              >
                                <MenuItem value={1}>Monday</MenuItem>
                                <MenuItem value={2}>Tuesday</MenuItem>
                                <MenuItem value={3}>Wednesday</MenuItem>
                                <MenuItem value={4}>Thursday</MenuItem>
                                <MenuItem value={5}>Friday</MenuItem>
                                <MenuItem value={6}>Saturday</MenuItem>
                                <MenuItem value={0}>Sunday</MenuItem>
                              </Select>
                            </FormControl>
                          )}
                        />
                      </>
                    )}

                    <Typography variant="body2">at</Typography>
                    <Controller
                      name="schedule.time"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="time"
                          size="small"
                          sx={{ minWidth: 180 }}
                          InputLabelProps={{ shrink: true, required: true }}
                          required
                        />
                      )}
                    />
                  </Stack>
                )}
              </Stack>

              {/* Timezone */}
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Timezone
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the timezone for this rule
              </Typography>
              <Controller
                name="schedule.timezone"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" sx={{ minWidth: 320 }} required>
                    <Select
                      defaultValue="Asia/Kolkata"
                      size="small"
                      placeholder="Select Timezone"
                      className="my-custom-selector"
                      {...field}
                      displayEmpty
                      inputProps={{ required: true }}
                    >
                      <MenuItem value="Asia/Kolkata">Asia/Kolkata (India Standard Time)</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              <Divider sx={{ mt: 4, mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 0 }}>
                Controlled Schedule
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                (Optional) You can set a start and/or end date for this automation, or leave them
                blank to run indefinitely.
              </Typography>

              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 4, maxWidth: 500 }}>
                <Controller
                  name="schedule.startDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Start Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={!!errors.schedule?.startDate}
                      helperText={errors.schedule?.startDate?.message}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}
                />
                {/* End Date */}
                <Controller
                  name="schedule.endDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="End Date"
                      type="date"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={!!errors.schedule?.endDate}
                      helperText={errors.schedule?.endDate?.message}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}
                />
              </Stack>
            </Paper>
          </Grid>

          {/* Execution Type (Control) */}
          <Grid item xs={12} mt={2}>
            <Stack mb={1}>
              <Typography variant="subtitle2" sx={{ mb: 0 }}>
                Control <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                Determine the level of control over the actions of this rule
              </Typography>
            </Stack>
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
              <Controller
                name="executionType"
                control={control}
                defaultValue="MANUAL"
                render={({ field }) => (
                  <RadioGroup {...field} column>
                    <FormControlLabel
                      value="MANUAL"
                      control={
                        <Radio
                          checked={field.value === 'MANUAL'}
                          color="primary"
                          sx={{ mr: 1, mt: -0.5 }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={700} sx={{ mb: 0 }}>
                            Manual
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Manually approve rule actions on the Suggestions page
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', m: 0, mb: 2 }}
                    />

                    <FormControlLabel
                      value="AUTO"
                      control={
                        <Radio
                          checked={field.value === 'AUTO'}
                          color="primary"
                          sx={{ mr: 1, mt: -0.5 }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={700} sx={{ mb: 0 }}>
                            Automate
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Automate this rule to have Helium 10 Ads automatically apply rule
                            actions
                          </Typography>
                        </Box>
                      }
                      sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                  </RadioGroup>
                )}
              />
            </Paper>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
}
